import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { SuiDropdownDirective, SuiTooltipDirective } from '@semeris/ui';
import { AppActions } from 'projects/semeris/src/store/app/app.actions';
import { take } from 'rxjs/operators';

import { MemoShortName } from '../../../memo/models/memo.model';
import { MemoService } from '../../../memo/services/memo.service';
import { PermissionsDirective } from '../../../shared/directives/permissions.directive';
import { Permission } from '../../../shared/models/api/permissions.enum';
import { UserDateTimeFormatPipe } from '../../../shared/pipes/user-date-time-format.pipe';
import { PermissionService } from '../../../shared/services/permission.service';

@Component({
  selector: 'semeris-manager-approval-dropdown',
  imports: [SuiDropdownDirective, SuiTooltipDirective, PermissionsDirective, UserDateTimeFormatPipe],
  templateUrl: './manager-approval-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManagerApprovalDropdownComponent {
  private readonly memoService = inject(MemoService);
  private readonly store = inject(Store);
  private readonly permissionService = inject(PermissionService);

  public readonly Permissions = Permission;

  public readonly content = signal('');
  public readonly updatedAt = signal<string | null>(null);
  public readonly isLoading = signal(false);
  public readonly isSaving = signal(false);
  public readonly loadFailed = signal(false);

  private readonly savedContent = signal('');

  private readonly userPermissions = toSignal(this.permissionService.permissions$, { initialValue: [] });

  /** Read-only viewers still get the memo — the textarea locks rather than disappearing. */
  public readonly canWrite = computed(() => this.userPermissions().includes(Permission.WRITE_MEMO));

  public readonly isDirty = computed(() => this.content() !== this.savedContent());

  public onOpen(): void {
    // An unsaved draft outlives the panel — refetching here would silently discard it.
    if (this.isDirty()) {
      return;
    }

    this.isLoading.set(true);
    this.loadFailed.set(false);

    this.memoService
      .getMemoByShortName(MemoShortName.CatalogManagerApproval)
      .pipe(take(1))
      .subscribe({
        next: (memo) => {
          this.applyMemo(memo.content ?? '', memo.updatedAt ?? null);
          this.isLoading.set(false);
        },
        error: (error: HttpErrorResponse) => {
          // 404 is the never-written memo, not a failure — anything else is.
          if (error.status === 404) {
            this.applyMemo('', null);
          } else {
            this.loadFailed.set(true);
          }
          this.isLoading.set(false);
        },
      });
  }

  public onContentInput(value: string): void {
    this.content.set(value);
  }

  public onSave(): void {
    this.isSaving.set(true);

    this.memoService
      .upsertMemo(MemoShortName.CatalogManagerApproval, this.content())
      .pipe(take(1))
      .subscribe({
        next: (memo) => {
          this.applyMemo(memo.content ?? '', memo.updatedAt ?? null);
          this.isSaving.set(false);
          this.store.dispatch(AppActions.PushNotification({ message: 'Manager approval updated', config: 'success' }));
        },
        error: () => {
          this.isSaving.set(false);
          this.store.dispatch(AppActions.PushNotification({ message: 'Failed to update manager approval', config: 'fail' }));
        },
      });
  }

  private applyMemo(content: string, updatedAt: string | null): void {
    this.content.set(content);
    this.savedContent.set(content);
    this.updatedAt.set(updatedAt);
  }
}
